import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import type { SiteSettings } from '../../lib/types';
import { MediaPicker } from '../../components/ui/MediaPicker';
import GoogleBusinessTab from '../settings/GoogleBusinessTab';
import SearchConsoleTutorial from '../settings/SearchConsoleTutorial';
import ContextCard from '../../components/ui/ContextCard';
import PageContainer from '../../components/ui/PageContainer';
import PageHeader from '../../components/ui/PageHeader';
import PublishBanner from '../../components/ui/PublishBanner';

type Tab = 'seo' | 'geo' | 'resenas' | 'buscadores';

const CONTEXT: Record<Tab, { title: string; description: string; detail: string }> = {
  seo: {
    title: '¿Qué configura SEO?',
    description: 'Define cómo se presenta tu sitio en buscadores, principalmente mediante un título y una descripción generales.',
    detail: 'Describe con claridad qué ofrece tu negocio y dónde opera. Evita listas de palabras repetidas.',
  },
  geo: {
    title: '¿Qué configura GEO / IA?',
    description: 'Ayuda a buscadores y asistentes de inteligencia artificial a entender qué hace tu negocio, dónde trabaja y en qué temas tiene experiencia.',
    detail: 'Usa términos concretos y reales. Esta información complementa el contenido del sitio, pero no garantiza una posición específica en resultados.',
  },
  resenas: {
    title: '¿Qué es la conexión con Google?',
    description: 'Trae tu calificación y tus reseñas de Google Maps para mostrarlas en tu sitio y animar a tus clientes a dejarte una reseña.',
    detail: 'Solo leemos datos públicos de tu ficha; no necesitas darnos acceso a tu cuenta de Google. Publica el sitio para que los cambios sean visibles.',
  },
  buscadores: {
    title: '¿Para qué sirve esto?',
    description: 'Google Search Console te muestra si tu sitio aparece en los resultados de búsqueda y cuántas personas hacen clic en él.',
    detail: 'Sigue los pasos en orden. Puedes hacerlos en distintos momentos — tu progreso se guarda automáticamente.',
  },
};

// ─── Types ─────────────────────────────────────────────────────────────────────
interface SeoData { title: string; description: string }
interface GeoData { areaServed: string[]; knowsAbout: string[]; priceRange: string }

function emptySeo(): SeoData { return { title: '', description: '' }; }
function emptyGeo(): GeoData { return { areaServed: [], knowsAbout: [], priceRange: '' }; }
function parse<T>(json: string | null | undefined, fallback: T): T {
  try { return JSON.parse(json ?? '') as T; } catch { return fallback; }
}

// ─── ChipInput ─────────────────────────────────────────────────────────────────
function ChipInput({ values, onChange, placeholder }: { values: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [input, setInput] = useState('');
  function add() {
    const trimmed = input.trim();
    if (!trimmed || values.includes(trimmed)) { setInput(''); return; }
    onChange([...values, trimmed]);
    setInput('');
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {values.map((v) => (
          <span key={v} className="flex items-center gap-1 bg-ui-surface border border-ui-border rounded-full px-2.5 py-0.5 text-xs text-ui-text">
            {v}
            <button onClick={() => onChange(values.filter((x) => x !== v))} className="text-ui-text-subtle hover:text-ui-danger leading-none">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 bg-ui-bg border border-ui-border rounded px-3 py-1.5 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
        <button onClick={add} className="px-3 py-1.5 bg-ui-surface border border-ui-border rounded text-xs text-ui-text hover:border-ui-accent">
          Añadir
        </button>
      </div>
    </div>
  );
}

// ─── SeoPage ───────────────────────────────────────────────────────────────────
export default function SeoPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>((searchParams.get('tab') as Tab) ?? 'seo');
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [seo, setSeo] = useState<SeoData>(emptySeo());
  const [geo, setGeo] = useState<GeoData>(emptyGeo());
  const [saving, setSaving] = useState(false);
  const [needsPublish, setNeedsPublish] = useState(false);
  const [siteUrl, setSiteUrl] = useState('https://tusitio.com');
  const [tenantId, setTenantId] = useState('default');

  useEffect(() => {
    api.settings.get().then((s) => {
      if (!s) return;
      setSettings(s);
      setSeo(parse(s.seoJson, emptySeo()));
      setGeo({ ...emptyGeo(), ...parse(s.geoJson, emptyGeo()) });
      setTenantId(String(s.tenantId));
    }).catch(console.error);
    api.settings.getSite().then((s) => {
      if (!s) return;
      const url = s.domain
        ? `https://${s.domain.replace(/^https?:\/\//, '')}`
        : s.pagesProject
          ? `https://${s.pagesProject}.pages.dev`
          : 'https://tusitio.com';
      setSiteUrl(url);
    }).catch(console.error);
  }, []);

  async function save() {
    if (!settings) return;
    setSaving(true);
    try {
      const updated = await api.settings.update({
        ...settings,
        seoJson: JSON.stringify(seo),
        geoJson: JSON.stringify(geo),
      });
      setSettings(updated);
      setNeedsPublish(true);
    } finally {
      setSaving(false);
    }
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'seo', label: 'SEO' },
    { id: 'geo', label: 'GEO / IA' },
    { id: 'resenas', label: 'Reseñas de Google' },
    { id: 'buscadores', label: 'Aparecer en Google' },
  ];

  return (
    <PageContainer>
      <PageHeader title="SEO y visibilidad" />

      <div className="flex overflow-x-auto [&::-webkit-scrollbar]:hidden border-b border-ui-border">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm border-b-2 transition-colors ${
              tab === t.id ? 'border-ui-accent text-ui-text' : 'border-transparent text-ui-text-muted hover:text-ui-text'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <ContextCard {...CONTEXT[tab]} />

      <PublishBanner needsPublish={needsPublish} onPublished={() => setNeedsPublish(false)} />

      {/* ── SEO ──────────────────────────────────────────────────────────────── */}
      {tab === 'seo' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">Título SEO por defecto</label>
            <input value={seo.title} onChange={(e) => setSeo({ ...seo, title: e.target.value })}
              placeholder="Nombre del sitio — Tagline"
              className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
          </div>
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">Descripción SEO por defecto</label>
            <textarea value={seo.description} onChange={(e) => setSeo({ ...seo, description: e.target.value })}
              placeholder="Descripción del sitio para buscadores…" rows={3}
              className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent resize-none" />
          </div>
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">Imagen al compartir en redes (Open Graph)</label>
            <MediaPicker
              value={settings?.ogImage ? [settings.ogImage] : []}
              onChange={(urls) => setSettings((s) => s ? { ...s, ogImage: urls[0] ?? null } : s)}
              multiple={false} folder="settings" />
            <p className="text-sm text-ui-text-subtle mt-1">Se usa al compartir en redes sociales cuando la página no tiene imagen propia.</p>
          </div>
          <p className="text-sm text-ui-text-subtle">
            Estos valores se usan cuando una página no tiene SEO propio configurado.
          </p>
        </div>
      )}

      {/* ── GEO / IA ─────────────────────────────────────────────────────────── */}
      {tab === 'geo' && (
        <div className="space-y-5">
          <p className="text-xs text-ui-text-muted">
            Estos datos enriquecen el schema.org de tu sitio para que ChatGPT, Perplexity y Google AI
            te reconozcan como experto local.
          </p>
          <div>
            <label className="block text-sm text-ui-text-muted mb-2">Zonas de servicio</label>
            <ChipInput values={geo.areaServed} onChange={(v) => setGeo({ ...geo, areaServed: v })}
              placeholder="Ej: Polanco, Lomas de Chapultepec…" />
            <p className="text-sm text-ui-text-subtle mt-1">Barrios o colonias donde operas. Escribe y pulsa Enter para añadir.</p>
          </div>
          <div>
            <label className="block text-sm text-ui-text-muted mb-2">Especialidades</label>
            <ChipInput values={geo.knowsAbout} onChange={(v) => setGeo({ ...geo, knowsAbout: v })}
              placeholder="Ej: propiedades de lujo, desarrollos residenciales…" />
            <p className="text-sm text-ui-text-subtle mt-1">Qué tipos de propiedad o servicios dominas.</p>
          </div>
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">Rango de precios</label>
            <input value={geo.priceRange} onChange={(e) => setGeo({ ...geo, priceRange: e.target.value })}
              placeholder="Desde $5M MXN"
              className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
          </div>
        </div>
      )}

      {(['seo', 'geo'] as Tab[]).includes(tab) && (
        <div className="pt-2">
          <button onClick={save} disabled={saving || !settings}
            className="px-4 py-2 bg-ui-accent text-ui-text-inverse text-sm rounded-md disabled:opacity-50 hover:opacity-90">
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      )}

      {tab === 'resenas' && <GoogleBusinessTab />}

      {tab === 'buscadores' && (
        <SearchConsoleTutorial siteUrl={siteUrl} tenantId={tenantId} />
      )}
    </PageContainer>
  );
}
